/**
 * Submission + analytics service for the school outreach pages.
 *
 * Both form types go to dedicated Cloud Functions (functions/src/outreach.ts),
 * which validate, verify reCAPTCHA and write to server-only Firestore
 * collections. A submission only counts as successful once the callable
 * resolves — nothing is persisted or faked client-side.
 *
 * Analytics are first-party aggregate counters (no cookies, no identifiers,
 * no personal data). Google Analytics runs only on the static marketing pages
 * in public/, not on these school pages.
 */
import { httpsCallable } from 'firebase/functions';
import { functions, usingFunctionsEmulator } from './firebase';
import { executeRecaptchaV3 } from './recaptcha';
import type { SchoolConfig } from '../content/schools';

export interface OutreachAttribution {
  schoolSlug: string;
  referralCode: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  /** Value of a `?ref=` param, if the link carried one. */
  refParam?: string;
  landingPath: string;
  /** Hostname only — never the full referring URL. */
  referrerHost?: string;
}

export interface BetaApplicationInput {
  firstName: string;
  email: string;
  specialty: string;
  businessStage: string;
  graduationTimeline: string;
  schoolOrProgram: string;
  mainChallenge: string;
  consentToContact: boolean;
  /** Honeypot — must stay empty. */
  hfsCheck: string;
}

export interface SchoolInquiryInput {
  name: string;
  workEmail: string;
  organization: string;
  role: string;
  preferredCollaboration: string;
  website: string;
  audienceSize: string;
  details: string;
  consentToRespond: boolean;
  /** Honeypot — must stay empty. */
  hfsCheck: string;
}

export type SubmissionResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'validation' | 'unavailable' | 'rate_limited' | 'error';
      fieldErrors?: Record<string, string>;
    };

const UTM_KEYS = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
} as const;

export function readAttribution(school: SchoolConfig, search: string, pathname: string): OutreachAttribution {
  const params = new URLSearchParams(search);
  const attribution: OutreachAttribution = {
    schoolSlug: school.slug,
    referralCode: school.referralCode,
    landingPath: pathname,
  };
  for (const [param, key] of Object.entries(UTM_KEYS)) {
    const value = params.get(param)?.trim();
    if (value) attribution[key] = value.slice(0, 200);
  }
  const ref = params.get('ref')?.trim();
  if (ref) attribution.refParam = ref.slice(0, 100);

  try {
    if (document.referrer) {
      const host = new URL(document.referrer).hostname;
      if (host && host !== window.location.hostname) attribution.referrerHost = host;
    }
  } catch {
    // Malformed referrer — ignore.
  }
  return attribution;
}

function mapCallableError(err: unknown): SubmissionResult {
  const e = err as { code?: string; message?: string; details?: unknown };
  const code = e?.code ?? '';
  if (code === 'functions/invalid-argument') {
    const details = e.details as { fieldErrors?: Record<string, string> } | undefined;
    return { ok: false, reason: 'validation', fieldErrors: details?.fieldErrors };
  }
  if (code === 'functions/resource-exhausted') return { ok: false, reason: 'rate_limited' };
  if (code === 'functions/failed-precondition' || code === 'functions/not-found') {
    return { ok: false, reason: 'unavailable' };
  }
  return { ok: false, reason: 'error' };
}

async function getRecaptchaToken(action: string): Promise<string | null> {
  try {
    return await executeRecaptchaV3(action);
  } catch (err) {
    console.error('reCAPTCHA unavailable:', err);
    return null;
  }
}

async function submit(
  callableName: string,
  recaptchaAction: string,
  input: object,
  attribution: OutreachAttribution,
): Promise<SubmissionResult> {
  const recaptchaToken = await getRecaptchaToken(recaptchaAction);
  if (!recaptchaToken) return { ok: false, reason: 'unavailable' };

  try {
    const callable = httpsCallable(functions, callableName);
    const res = await callable({ ...input, attribution, recaptchaToken });
    const data = res.data as { success?: boolean } | undefined;
    if (data?.success !== true) return { ok: false, reason: 'error' };
    return { ok: true };
  } catch (err) {
    console.error(`${callableName} failed:`, err);
    return mapCallableError(err);
  }
}

export function submitBetaApplication(
  input: BetaApplicationInput,
  attribution: OutreachAttribution,
): Promise<SubmissionResult> {
  return submit('submitBetaApplication', 'beta_application', input, attribution);
}

export function submitSchoolInquiry(
  input: SchoolInquiryInput,
  attribution: OutreachAttribution,
): Promise<SubmissionResult> {
  return submit('submitSchoolInquiry', 'school_inquiry', input, attribution);
}

// ============================================================================
// ANALYTICS
// ============================================================================

/** Submissions are counted server-side when they're persisted, not here. */
export type OutreachEvent =
  | 'page_view'
  | 'student_cta_click'
  | 'partner_cta_click'
  | 'student_application_start'
  | 'school_inquiry_start';

const sentPageViews = new Set<string>();

/**
 * Fire-and-forget aggregate counter. Sends only the event name and school slug.
 * Disabled in local dev (unless pointed at the emulator) so test traffic
 * doesn't skew production counts.
 */
export function trackOutreachEvent(event: OutreachEvent, schoolSlug: string): void {
  if (event === 'page_view') {
    // Dedupe StrictMode double-mounts and re-renders within one page load.
    if (sentPageViews.has(schoolSlug)) return;
    sentPageViews.add(schoolSlug);
  }

  if (!import.meta.env.PROD && !usingFunctionsEmulator) {
    console.debug('[outreach analytics]', event, schoolSlug);
    return;
  }

  const callable = httpsCallable(functions, 'recordOutreachEvent');
  callable({ event, schoolSlug }).catch(() => {
    // Analytics must never affect the page.
  });
}
